import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, BellRing } from "lucide-react";

function urlB64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  let reg = await navigator.serviceWorker.getRegistration();
  if (!reg) { try { reg = await navigator.serviceWorker.register("/sw.js"); } catch (e) { return null; } }
  return navigator.serviceWorker.ready.then(() => reg).catch(() => reg);
}

export default function PushToggleButton({ className = "" }) {
  const supported = typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!supported) return;
    (async () => {
      try {
        const reg = await getRegistration();
        if (!reg) return;
        const sub = await reg.pushManager.getSubscription();
        setSubscribed(!!sub);
      } catch (e) { /* ignore */ }
    })();
  }, [supported]);

  if (!supported) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      if (perm !== "granted") { toast.error("Permesso notifiche negato dal browser"); return; }
      const reg = await getRegistration();
      if (!reg) { toast.error("Service worker non disponibile"); return; }
      const { data } = await api.get("/push/vapid-public-key");
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlB64ToUint8Array(data.public_key),
        });
      }
      await api.post("/push/subscribe", sub.toJSON());
      setSubscribed(true);
      toast.success("Notifiche attivate su questo dispositivo");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Impossibile attivare le notifiche");
    } finally { setBusy(false); }
  };

  return (
    <Button data-testid="push-toggle" variant="ghost" size="sm" onClick={enable} disabled={busy || subscribed} className={className}>
      {subscribed ? <BellRing size={16} className="sm:mr-1.5" /> : <Bell size={16} className="sm:mr-1.5" />}
      <span className="hidden sm:inline">{subscribed ? "Notifiche attive ✓" : (busy ? "Attivo…" : "Attiva notifiche")}</span>
    </Button>
  );
}
